import React, { useState, useCallback } from 'react';
import './VectorUpdateLog.css';
import PageHeader from '../components/PageHeader';
import { IconCheck, IconZap } from '../components/Icons';
import { API_ENDPOINTS } from '../config';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { useAuth } from '../contexts/AuthContext';
import { getBrandIdentity, useBrandIdentity } from '../utils/brandIdentity';

/** Newest first */
const getUpdates = (appName) => [
  {
    id: '2026-09-17',
    dateLabel: 'September 17, 2026',
    title: 'Sharper My Portfolio numbers & Overview settings',
    lead:
      'My Portfolio now reconciles your open positions, lets you step back through past months, and reads Alpaca accounts more accurately — and Overview gets clearer monthly-return and benchmark options.',
    bullets: [
      'My Portfolio: open positions are reconciled against your trades so holdings and P&L line up',
      'My Portfolio: step through trade history month by month',
      'Alpaca: deposits and withdrawals are now accounted for, so returns reflect real performance rather than cash moving in and out',
      'Alpaca: connection health is shown on the account so you know when something needs attention',
      'Overview: monthly returns can be shown as Fixed $100k, Account %, or Futures, with selectable benchmarks to compare against',
      'Faster, steadier market quotes across Stocks, World Markets, and symbol pages',
    ],
  },
  {
    id: '2026-08-27',
    dateLabel: 'August 27, 2026',
    title: 'Hedging Risk & a Risk Tools hub on mobile',
    lead:
      'A new tool for spotting strategies that cancel each other out, and an easier way to reach every risk tool on your phone.',
    bullets: [
      'New Hedging Risk page (Pro) — finds strategy pairs that trade opposite each other so their combined P&L cancels out',
      'New Risk Tools tab in the mobile bottom bar — Blowout Risk, % Rule Risk, and Hedging Risk in one place',
      'Mobile header now tucks away as you scroll for more screen space',
    ],
  },
  {
    id: '2026-08-25',
    dateLabel: 'August 25, 2026',
    title: 'My Portfolio redesign, NinjaTrader accounts & symbol pages',
    lead:
      'My Portfolio becomes a full portfolio dashboard — connect your NinjaTrader / Tradovate accounts, see holdings and benchmarks at a glance, and click into any symbol for details.',
    bullets: [
      'Connect NinjaTrader / Tradovate accounts — sign in through Tradovate or use API keys; live and demo accounts connect separately',
      'NinjaTrader tiles show net liquidity and open P&L, with a banner when an account needs to be reconnected',
      'New portfolio table with sparklines and a holdings donut chart',
      'Benchmark your portfolio against SPY, QQQ, IEF, and GLD over any date range',
      'Recent Trades and a daily P&L trade calendar',
      'Stocks watchlist, World Markets map, and Market Hours panels',
      'Switch between portfolios right from the detail page',
      'New symbol pages (e.g. /symbol/NVDA) — click any Stocks or World Markets row for the quote, session stats, day and 52-week range, price history, and a short summary',
      'Sign-in, upgrade, and connect prompts now show a sample portfolio so you can see what you get first',
      'New light mode — switch between dark and light from the sidebar',
      'Monthly returns explainer and the traded instrument shown for each strategy',
    ],
  },
  {
    id: '2026-08-04',
    dateLabel: 'August 4, 2026',
    title: 'A new sign-in',
    lead:
      'A refreshed sign-in screen, and your results now carry your brand\'s name.',
    bullets: [
      'Results are labelled with your brand\'s name',
      'New sign-in screen',
    ],
  },
  {
    id: '2026-07-19',
    dateLabel: 'July 19, 2026',
    title: 'My Portfolio: trade history, smart tiles & charts',
    lead:
      'My Portfolio goes much deeper on each connected account, plus a clear breakdown of what every plan includes.',
    bullets: [
      'Trade History — a per-account table of every trade',
      'Smart tiles for Gain (monthly, yearly, drawdown), Equity (investment, holdings, cash), and Profit',
      'Period stat cards that summarize each period at a glance',
      'Chart tabs for Equity, Growth, Profit, and Drawdown',
      'Coinbase: support for the newer Coinbase API keys and more accurate trade history',
      'My Portfolio is a Pro feature — Basic members see a PRO badge and an upgrade prompt',
      'New plan comparison showing exactly what Basic, Pro, and Max include',
      'Embeddable performance chart that brands can place on their own websites',
    ],
  },
  {
    id: '2026-06-30',
    dateLabel: 'June 30, 2026',
    title: 'New Overview, My Portfolio & Equity Portfolios',
    lead:
      'A rebuilt Overview, the first version of My Portfolio for your own broker accounts, and new blended equity portfolios in Results.',
    bullets: [
      'New Overview page — growth chart, growth calculator, calendar, monthly returns, key insights, trades list, comparison, and disclosures in one layout',
      `Introducing My Portfolio — connect your broker accounts (Coinbase, Alpaca, Interactive Brokers) and track your equity in ${appName}`,
      'New Equity Portfolio presets (1× and 2.25×) — blended stock-strategy portfolios in the Results simulator',
      'New OG Equity Portfolio presets (1× and 2.25×) — built on the historical OG curve and continuing from the live OG feed',
    ],
  },
  {
    id: '2026-05-23',
    dateLabel: 'May 23, 2026',
    title: 'Custom dashboards, Spreadsheet view & Multi-Strat',
    lead:
      `A big jump in what you can build and analyze inside ${appName} — your own dashboards on top of the shared brand dashboard, a raw-data spreadsheet view for every strategy, and a synthetic Multi-Strat that combines several strategies into a single view.`,
    bullets: [
      'New Dashboard page — every signed-in user lands on their brand\'s shared dashboard with KPI cards, calendar, weekly summary, and Week Win Rate breakdowns',
      'The brand dashboard supports a pinned banner message from Admin / Brand Admin that everyone sees at the top',
      'Pro and up: create up to 5 custom dashboards with KPI cards, calendars, cumulative charts, and a Current Results table — drag, resize, hide, and rename widgets freely on desktop',
      'KPI cards support Net Total, Win Rate, Week Win Rate, and Best Day metrics — one card per strategy + contract type',
      'Set any personal dashboard as default; the brand dashboard stays the fallback when none is set',
      'Spreadsheet view added to every strategy in Results — see the raw weekly grid alongside Calendar / Chart / Weekly views',
      'New Multi-Strat option in every strategy dropdown — combines different strategies on different weekdays into a single virtual strategy',
      'Multi-Strat works across the dashboard widgets, Blowout Risk, % Rule Risk, Bulk Risk, and CSV Upload',
      'Results: KPI summary block is now a Pro feature — Basic users see a clearly-labeled EXAMPLE preview with an upgrade CTA, the rest of the page (Calendar / Chart / Weekly / Spreadsheet) is open to everyone',
      'Portfolio Simulator gate now shows a sample ranked table behind the upgrade card so non-Pro viewers can see what the feature looks like',
      'New Brand Admin tier — same permissions as Max plus the ability to edit the brand dashboard layout and message; Admin tier also has this',
      'Mobile polish: KPI cards render as squares with all five weekday win rates visible, Current Results rows scale down, calendar monthly stats sit inline with the date nav',
    ],
  },
  {
    id: '2026-05-08',
    dateLabel: 'May 8, 2026',
    title: 'Major UI refresh & strategy simulator',
    lead:
      'A large pass on layout, navigation, and visual hierarchy so the app feels faster to scan and easier to live in every day.',
    bullets: [
      `Re-organized the overall look and navigation across ${appName}`,
      'Polished dashboard and tool surfaces for a more cohesive dark UI',
      'Introduced the strategy simulator for strategy benchmarks vs major indices and futures',
    ],
  },
  {
    id: '2026-03-30',
    dateLabel: 'March 30, 2026',
    title: 'Roll-over week, red-folder news & Filter / Compare',
    lead:
      'More ways to slice strategy data with the calendar context prop traders actually care about.',
    bullets: [
      'Roll-over week filter',
      'Red-folder (high-impact news) filter',
      'Filter & Compare by weekday, roll week, and red-folder days — across Calendar, Weekly, and Chart views',
      'Charts: date range control for focusing on a period',
      'Calendar: clickable trading days and weeks for quicker drill-down',
    ],
  },
  {
    id: '2026-03-23',
    dateLabel: 'March 23, 2026',
    title: 'Initial launch',
    lead:
      `First public drop: everyone with access could sign in and use ${appName} alongside their strategy results.`,
    bullets: [
      'Modern dashboard for daily and weekly performance in one place',
      'Compare strategies and drill into what is working',
      'Blowout Risk and % Rule (consistency) checks when sizing trades',
      'Pro plan at $20/mo with a 7-day free trial (via Whop checkout)',
    ],
    footnote:
      `${appName} provides educational analytics and risk assessments — not financial advice. Past performance does not guarantee future results.`,
  },
];

function VectorUpdateLog({ onNavigate, user }) {
  const { appName } = useBrandIdentity();
  const { isPro, isMax, isAdmin, tierReady } = useAuth();
  const showProTrialCta =
    tierReady && Boolean(String(user?.email || '').trim()) && !isPro && !isMax && !isAdmin;
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState(null);

  const openWhopCheckout = useCallback(async () => {
    setCheckoutLoading(true);
    setCheckoutError(null);
    try {
      const healthCheck = await fetch(API_ENDPOINTS.health);
      if (!healthCheck.ok) {
        throw new Error(`${getBrandIdentity().appName} is temporarily unavailable. Please try again shortly.`);
      }
      const response = await fetch(API_ENDPOINTS.subscriptionCreateCheckout, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getBearerAuthHeaders(),
        },
        body: JSON.stringify({ email: user?.email || '', useVectorCoupon: false }),
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(data?.error || data?.message || 'Could not start checkout');
      }
      if (data?.checkoutUrl) {
        window.location.href = data.checkoutUrl;
        return;
      }
      throw new Error('No checkout URL received');
    } catch (e) {
      console.error(e);
      setCheckoutError(e.message || 'Could not open checkout');
      setCheckoutLoading(false);
    }
  }, [user?.email]);

  return (
    <>
      <PageHeader onNavigate={onNavigate} />
      <div className="vector-update-log-page">
        <div className="vector-update-log-container">
          <p className="vector-update-eyebrow">
            <span className="vector-update-eyebrow-icon" aria-hidden="true"><IconZap size={16} /></span> {appName}
          </p>
          <h1 className="vector-update-heading">{appName} Update Log</h1>
          <p className="vector-update-intro">
            What shipped in the product, newest first. We add entries here when we release meaningful changes.
          </p>

          {showProTrialCta ? (
            <>
              <div className="vector-update-actions">
                <button
                  type="button"
                  className="vector-update-btn primary"
                  onClick={openWhopCheckout}
                  disabled={checkoutLoading}
                >
                  {checkoutLoading ? 'Opening checkout…' : 'Start 7-day Pro trial'}
                </button>
              </div>
              {checkoutError ? (
                <p className="vector-update-error" role="alert">
                  {checkoutError}
                </p>
              ) : null}
            </>
          ) : null}

          <div className="vector-update-list">
            {getUpdates(appName).map((u) => (
              <article key={u.id} className="vector-update-card">
                <time className="vector-update-date" dateTime={u.id}>
                  {u.dateLabel}
                </time>
                <h2 className="vector-update-title">{u.title}</h2>
                <p className="vector-update-lead">{u.lead}</p>
                <ul className="vector-update-bullets">
                  {u.bullets.map((b) => (
                    <li key={b}>
                      <IconCheck size={16} /> {b}
                    </li>
                  ))}
                </ul>
                {u.footnote ? <p className="vector-update-footnote">{u.footnote}</p> : null}
              </article>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

export default VectorUpdateLog;
